import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Quote, ChevronLeft, ChevronRight, Check, X } from "lucide-react";
import {
  Reveal, MaskLine, SectionLabel, Counter, Magnetic, Marquee,
} from "../components/primitives";
import {
  IMAGES, STATS, WHY, SOLUTIONS, PRODUCTS, CLIENTS, BRANDS, WORK, INSIGHTS, TESTIMONIALS,
} from "../data/site";

/* ---------------- HERO ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  return (
    <section ref={ref} className="relative pt-[120px] md:pt-[150px] pb-16 md:pb-24 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-[6px] w-[6px] rounded-full bg-[#f4b41a]" />
          <span className="text-xs uppercase tracking-[0.25em] text-neutral-500">Your one stop solution vendor for everything</span>
        </div>

        <h1 className="font-display font-medium tracking-tighter leading-[0.9] text-[16vw] sm:text-[13vw] lg:text-[10.5vw]">
          <MaskLine delay={0.05}>One partner.</MaskLine>
          <MaskLine delay={0.18} className="text-[#f4b41a]">Endless</MaskLine>
          <MaskLine delay={0.31}>possibilities.</MaskLine>
        </h1>

        <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="md:col-span-5 text-lg sm:text-xl text-neutral-600 leading-relaxed">
            Corporate merchandise, gifting, rewards and engagement solutions — brought together under one roof.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
            className="md:col-span-7 md:justify-self-end flex flex-wrap gap-4">
            <Magnetic>
              <Link to="/solutions" data-testid="hero-solutions-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-neutral-900 text-white font-semibold px-7 py-4 hover:bg-neutral-800 transition-colors">
                Explore Solutions <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
            <Magnetic>
              <Link to="/inquire" data-testid="hero-inquire-cta"
                className="group inline-flex items-center gap-2 rounded-full border border-neutral-300 font-semibold px-7 py-4 hover:border-neutral-900 transition-colors">
                Inquire Now <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Magnetic>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-12 md:mt-16 border-y border-neutral-200 py-5 overflow-hidden">
          <div className="flex items-center gap-6">
            <span className="hidden sm:inline shrink-0 text-xs uppercase tracking-[0.2em] text-neutral-400 font-semibold">Everything we do</span>
            <Marquee items={SOLUTIONS} dur="45s" render={(s) => (
              <span className="mx-4 inline-flex items-center gap-3 text-base md:text-lg text-neutral-700 whitespace-nowrap">
                <span className="h-[5px] w-[5px] rounded-full bg-[#f4b41a]" />{s.title}
              </span>
            )} />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- PROBLEM ---------------- */
const WITHOUT = [
  "Chasing 5+ different suppliers",
  "Multiple contracts & invoices",
  "Constant follow-ups & coordination",
  "Inconsistent quality & branding",
  "No single point of accountability",
];
const WITHV = [
  "One partner for every requirement",
  "One contract, one invoice",
  "A single point of contact",
  "Consistent, on-brand quality",
  "Pan-India delivery, handled for you",
];

const Problem = () => (
  <section className="py-24 md:py-32 bg-[#f8f9fa]">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10">
      <div className="text-center max-w-3xl mx-auto">
        <SectionLabel><span className="mx-auto flex items-center gap-3">The Difference</span></SectionLabel>
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02]">
            Too many vendors? <span className="text-[#f4b41a]">One simple solution.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-5 text-lg text-neutral-600">The difference between managing every requirement yourself and partnering with VETAS.</p>
        </Reveal>
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
        {/* WITHOUT VETAS */}
        <Reveal className="rounded-2xl border border-neutral-200 bg-white p-8 md:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-neutral-100 text-neutral-500 px-4 py-2 text-sm font-semibold uppercase tracking-[0.15em]">
            <X size={16} /> Without VETAS
          </div>
          <p className="mt-6 font-display text-2xl md:text-3xl font-medium tracking-tight text-neutral-400">The hard way</p>
          <ul className="mt-6 space-y-4">
            {WITHOUT.map((t) => (
              <li key={t} className="flex items-start gap-3 text-neutral-500">
                <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-neutral-100 shrink-0"><X size={14} className="text-neutral-400" /></span>
                <span className="line-through decoration-neutral-300">{t}</span>
              </li>
            ))}
          </ul>
        </Reveal>

        {/* WITH VETAS */}
        <Reveal delay={0.15} className="rounded-2xl bg-neutral-900 text-white p-8 md:p-10 relative overflow-hidden ring-2 ring-[#f4b41a]">
          <div className="noise" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#f4b41a] text-neutral-900 px-4 py-2 text-sm font-semibold uppercase tracking-[0.15em]">
              <Check size={16} /> With VETAS
            </div>
            <p className="mt-6 font-display text-2xl md:text-3xl font-medium tracking-tight text-[#f4b41a]">The VETAS way</p>
            <ul className="mt-6 space-y-4">
              {WITHV.map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center h-6 w-6 rounded-full bg-[#f4b41a] shrink-0"><Check size={14} className="text-neutral-900" /></span>
                  <span className="text-white/90">{t}</span>
                </li>
              ))}
            </ul>
            <Link to="/inquire" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#f4b41a] text-neutral-900 font-semibold px-6 py-3 hover:bg-white transition-colors group">
              Start with one partner <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- WHY ---------------- */
const Why = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10">
      <SectionLabel>Why VETAS</SectionLabel>
      <Reveal>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight max-w-3xl">Built around your needs.</h2>
      </Reveal>
      <div className="mt-14 border-t border-neutral-200">
        {WHY.map((w, i) => (
          <Reveal key={w.n} delay={i * 0.05}>
            <div className="group grid md:grid-cols-12 gap-4 md:gap-8 items-baseline py-8 border-b border-neutral-200 hover:bg-[#f9f6f0] transition-colors duration-500 px-2 -mx-2 rounded-lg">
              <span className="md:col-span-1 font-display text-2xl text-[#f4b41a] font-medium">{w.n}</span>
              <h3 className="md:col-span-4 font-display text-2xl md:text-3xl font-medium tracking-tight">{w.title}</h3>
              <p className="md:col-span-6 text-neutral-600 leading-relaxed">{w.body}</p>
              <ArrowUpRight className="md:col-span-1 justify-self-end text-neutral-300 group-hover:text-neutral-900 transition-colors" size={22} />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- SOLUTIONS ---------------- */
const SolutionsGrid = () => (
  <section className="py-24 md:py-32 bg-[#f8f9fa]">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <SectionLabel>Solutions</SectionLabel>
          <Reveal><h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl">What can we build for you?</h2></Reveal>
        </div>
        <Link to="/solutions" className="inline-flex items-center gap-2 font-semibold link-underline">All solutions <ArrowRight size={16} /></Link>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.n} delay={(i % 3) * 0.08}>
            <Link to="/solutions" className="group block relative rounded-2xl overflow-hidden bg-white border border-neutral-200 h-full">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={s.img} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <span className="absolute top-4 left-4 font-display text-white text-3xl font-medium drop-shadow">{s.n}</span>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">{s.title}</h3>
                  <ArrowUpRight size={18} className="text-neutral-400 group-hover:text-[#f4b41a] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <p className="mt-2 text-sm text-neutral-600">{s.desc}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- PRODUCTS HORIZONTAL ---------------- */
const ProductScroll = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-72%"]);
  return (
    <section ref={ref} className="relative h-[320vh] bg-neutral-900 text-white">
      <div className="sticky top-0 h-screen flex flex-col justify-center overflow-hidden">
        <div className="max-w-[1500px] mx-auto w-full px-5 md:px-10">
          <SectionLabel dark>Products & Possibilities</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight mb-10">Breadth, not clutter.</h2>
        </div>
        <motion.div style={{ x }} className="flex gap-5 px-5 md:px-10">
          {PRODUCTS.map((p) => (
            <div key={p.tag} className="relative shrink-0 w-[78vw] sm:w-[52vw] lg:w-[34vw] rounded-2xl overflow-hidden">
              <div className="aspect-[4/5]">
                <img src={p.img} alt={p.title} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 p-7">
                <span className="text-[#f4b41a] text-sm font-semibold tracking-[0.3em]">{p.tag}</span>
                <h3 className="font-display text-3xl font-medium tracking-tight mt-2">{p.title}</h3>
                <p className="text-white/70 mt-2 text-sm">{p.items}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

/* ---------------- BRAND NETWORK ---------------- */
const chip = (label) => (
  <span className="mx-2 inline-flex items-center rounded-full border border-neutral-200 bg-white px-6 py-3 text-lg md:text-xl font-display font-medium text-neutral-400 hover:text-neutral-900 transition-colors">
    {label}
  </span>
);

const BrandNetwork = () => (
  <section className="py-24 md:py-32 overflow-hidden">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 mb-14">
      <SectionLabel>Brand Network</SectionLabel>
      <div className="flex flex-wrap items-end justify-between gap-8">
        <Reveal><h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl">A network built for possibilities.</h2></Reveal>
        <div className="flex gap-10">
          <div><div className="font-display text-5xl md:text-6xl font-medium text-[#f4b41a]"><Counter to={100} suffix="+" /></div><p className="text-sm text-neutral-500 mt-1">Brand Associations</p></div>
          <div><div className="font-display text-5xl md:text-6xl font-medium text-neutral-900"><Counter to={400} suffix="+" /></div><p className="text-sm text-neutral-500 mt-1">Voucher Brands</p></div>
        </div>
      </div>
    </div>
    <div className="space-y-4">
      <Marquee items={BRANDS.slice(0, 22)} dur="55s" render={chip} />
      <Marquee items={BRANDS.slice(20)} reverse dur="50s" render={chip} />
    </div>
  </section>
);

/* ---------------- CLIENTS ---------------- */
const clientLogo = (c) => (
  c.logo ? (
    <div className="mx-3 grid place-items-center h-24 md:h-28 w-44 md:w-52 rounded-2xl border border-neutral-200 bg-white px-7">
      <img src={c.logo} alt={c.name} loading="lazy" className="max-h-12 md:max-h-14 w-auto object-contain" />
    </div>
  ) : (
    <div className="mx-3 grid place-items-center h-24 md:h-28 w-44 md:w-52 rounded-2xl border border-neutral-200 bg-[#f8f9fa] px-7">
      <span className="font-display text-lg md:text-xl font-medium text-neutral-500 text-center leading-tight">{c.name}</span>
    </div>
  )
);

const ClientsIntro = () => (
  <section className="pt-16 md:pt-24 pb-10">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 text-center">
      <SectionLabel><span className="mx-auto flex items-center gap-3">Clients</span></SectionLabel>
      <Reveal>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight max-w-3xl mx-auto">
          Trusted by <span className="text-[#f4b41a]">25+</span> businesses across India.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="mt-5 text-lg text-neutral-500 max-w-xl mx-auto">
          From FMCG leaders to hospitality groups — corporate requirements, delivered through one partner.
        </p>
      </Reveal>
    </div>
  </section>
);

const ClientsWall = () => {
  const withLogos = CLIENTS.filter((c) => c.logo);
  const rest = CLIENTS.filter((c) => !c.logo);
  const half = Math.ceil(withLogos.length / 2);
  const row1 = withLogos.slice(0, half);
  const row2 = [...withLogos.slice(half), ...rest];
  return (
    <section className="py-14 md:py-16 border-b border-neutral-200 overflow-hidden">
      <div className="space-y-4">
        <Marquee items={row1} dur="40s" render={clientLogo} />
        <Marquee items={row2} reverse dur="46s" render={clientLogo} />
      </div>
    </section>
  );
};

/* ---------------- WORK ---------------- */
const WorkTeaser = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <SectionLabel>Our Work</SectionLabel>
          <Reveal><h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight max-w-2xl">From requirement to reality.</h2></Reveal>
        </div>
        <Link to="/work" className="inline-flex items-center gap-2 font-semibold link-underline">View all work <ArrowRight size={16} /></Link>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {WORK.map((w, i) => (
          <Reveal key={w.client} delay={(i % 2) * 0.1}>
            <Link to="/work" className={`group block relative rounded-2xl overflow-hidden ${i % 2 ? "md:mt-12" : ""}`}>
              <div className="aspect-[16/11] overflow-hidden">
                <img src={w.img} alt={w.client} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-80" />
              <div className="absolute bottom-0 p-7 text-white">
                <span className="text-[#f4b41a] text-xs uppercase tracking-[0.2em] font-semibold">{w.cat}</span>
                <h3 className="font-display text-2xl md:text-3xl font-medium tracking-tight mt-2">{w.client}</h3>
                <p className="text-white/80 mt-1 line-clamp-2 max-w-md">{w.requirement}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- SUSTAINABILITY ---------------- */
const steps = ["Discarded Plastic", "Recycled Material", "Fabric", "Apparel", "Corporate Merchandise"];
const Sustain = () => (
  <section className="py-24 md:py-32 bg-[#ebf4f1] relative overflow-hidden">
    <div className="noise opacity-20" />
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 relative">
      <SectionLabel>Sustainability</SectionLabel>
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal><h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">Better products. Better choices.</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-neutral-600 text-lg leading-relaxed max-w-lg">Our recycled-plastic apparel line transforms discarded plastic into premium corporate merchandise — style, comfort and environmental responsibility in one.</p></Reveal>
          <div className="mt-10 flex flex-wrap items-center gap-2">
            {steps.map((s, i) => (
              <React.Fragment key={s}>
                <Reveal delay={i * 0.08}><span className="rounded-full bg-white border border-emerald-200 px-4 py-2 text-sm font-medium text-emerald-900">{s}</span></Reveal>
                {i < steps.length - 1 && <ArrowRight size={16} className="text-emerald-600" />}
              </React.Fragment>
            ))}
          </div>
          <Link to="/sustainability" className="mt-10 inline-flex items-center gap-2 font-semibold text-emerald-900 group">Explore the range <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></Link>
        </div>
        <Reveal delay={0.15} className="grid grid-cols-2 gap-4">
          <img src={IMAGES.sustain1} alt="Sustainable" className="rounded-2xl object-cover aspect-[3/4] w-full" loading="lazy" />
          <img src={IMAGES.sustain2} alt="Sustainable" className="rounded-2xl object-cover aspect-[3/4] w-full mt-8" loading="lazy" />
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- SOCIAL IMPACT ---------------- */
const Impact = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-12 items-center">
      <Reveal className="relative rounded-2xl overflow-hidden">
        <img src={IMAGES.ngo1} alt="Ashirwad" className="w-full aspect-[4/3] object-cover" loading="lazy" />
      </Reveal>
      <div>
        <SectionLabel>Social Impact</SectionLabel>
        <Reveal><h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">Corporate giving that gives back.</h2></Reveal>
        <Reveal delay={0.1}><p className="mt-6 text-neutral-600 text-lg leading-relaxed max-w-lg">We work with <strong className="text-neutral-900">Ashirwad</strong>, a unit of the North India Cerebral Palsy Association (NICPA), supporting therapy services and promoting products made by the children of the NGO through corporate gifting.</p></Reveal>
        <Reveal delay={0.18}><p className="mt-4 text-neutral-500">Include these products in your gifting kits and let every requirement create real impact.</p></Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- STATS ---------------- */
const Stats = () => (
  <section className="py-20 bg-[#f4b41a]">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 divide-neutral-900/10">
      {STATS.map((s) => (
        <Reveal key={s.label}>
          <div className="text-center md:text-left">
            <div className="font-display text-6xl md:text-7xl font-medium tracking-tighter text-neutral-900"><Counter to={s.value} suffix={s.suffix} /></div>
            <p className="mt-2 text-sm font-medium uppercase tracking-[0.15em] text-neutral-800">{s.label}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------------- INSIGHTS ---------------- */
const InsightsTeaser = () => (
  <section className="py-24 md:py-32 bg-[#f8f9fa]">
    <div className="max-w-[1500px] mx-auto px-5 md:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <SectionLabel>Insights</SectionLabel>
          <Reveal><h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight">The VETAS Playbook.</h2></Reveal>
        </div>
        <Link to="/insights" className="inline-flex items-center gap-2 font-semibold link-underline">Read more <ArrowRight size={16} /></Link>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {INSIGHTS.map((a, i) => (
          <Reveal key={a.title} delay={i * 0.08}>
            <Link to="/insights" className="group block rounded-2xl bg-white border border-neutral-200 p-7 h-full hover:border-neutral-900 transition-colors">
              <div className="flex items-center justify-between mb-8">
                <span className="text-xs uppercase tracking-[0.2em] text-[#f4b41a] font-semibold">{a.cat}</span>
                <span className="text-xs text-neutral-400">{a.read}</span>
              </div>
              <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight leading-snug">{a.title}</h3>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-neutral-500 group-hover:text-neutral-900 transition-colors">Read article <ArrowUpRight size={15} /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- TESTIMONIALS ---------------- */
const Testimonials = () => {
  const [i, setI] = useState(0);
  const n = TESTIMONIALS.length;
  const go = (d) => setI((p) => (p + d + n) % n);
  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % n), 6000);
    return () => clearInterval(t);
  }, [n]);
  const t = TESTIMONIALS[i];
  return (
    <section className="py-24 md:py-32 bg-neutral-900 text-white relative overflow-hidden">
      <div className="noise" />
      <div className="max-w-[1100px] mx-auto px-5 md:px-10 relative">
        <SectionLabel dark>Testimonials</SectionLabel>
        <Quote className="text-[#f4b41a] mb-6" size={44} />
        <div className="min-h-[220px] md:min-h-[200px]">
          <AnimatePresence mode="wait">
            <motion.blockquote
              key={i}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
              className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-snug">
              "{t.quote}"
              <footer className="mt-8 text-base font-sans font-normal text-white/60">
                <span className="text-white font-medium">{t.author}</span> — {t.role}
              </footer>
            </motion.blockquote>
          </AnimatePresence>
        </div>
        <div className="mt-8 flex items-center gap-4">
          <button data-testid="testimonial-prev" onClick={() => go(-1)} aria-label="Previous"
            className="grid place-items-center h-11 w-11 rounded-full border border-white/20 hover:border-[#f4b41a] hover:text-[#f4b41a] transition-colors"><ChevronLeft size={18} /></button>
          <button data-testid="testimonial-next" onClick={() => go(1)} aria-label="Next"
            className="grid place-items-center h-11 w-11 rounded-full border border-white/20 hover:border-[#f4b41a] hover:text-[#f4b41a] transition-colors"><ChevronRight size={18} /></button>
          <div className="flex gap-2 ml-2">
            {TESTIMONIALS.map((_, idx) => (
              <button key={idx} onClick={() => setI(idx)} aria-label={`Go to ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all duration-500 ${idx === i ? "w-8 bg-[#f4b41a]" : "w-1.5 bg-white/30"}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <ClientsIntro />
    <Stats />
    <ClientsWall />
    <Problem />
    <SolutionsGrid />
    <BrandNetwork />
    <Testimonials />
    <Why />
    <ProductScroll />
    <WorkTeaser />
    <Sustain />
    <Impact />
    <InsightsTeaser />
  </div>
);

export default Home;
